import { Component, Inject } from '@angular/core';
import { MODAL_DATA, NgbActiveModal } from '@agilent/ui-element';
import { ACApiService } from '@app/api/ac-api.service';
import {
    OpenLabSystem,
    OpenLabSystemApiService
} from '@app/api/openlab-system-api.service';
import { FormGroup } from '@angular/forms';

@Component({
    selector: 'app-progress-dialog',
    template: `
        <div role="document">
            <div class="modal-content">
                <div class="modal-warning-header" *ngIf="dialogTitle && isWarningDialog">
                    <ag-icon-wrapper class="awf-icon" iconName="warning-icon"></ag-icon-wrapper>
                    <h2 class="h2-title">WARNING: {{dialogTitle}}</h2>
                </div>
                <div class="modal-header" *ngIf="dialogTitle && !isWarningDialog">
                    <h2 class="h2-title">{{dialogTitle}}</h2>
                </div>
                <ag-dialog-content class="dialog-content">
                    <div [innerHTML]="content"></div>
                    <div [hidden]="!enableTextConfirmation">
                        <ag-input
                            [(ngModel)]="confirmationText"
                            label="Type 'confirm' to proceed"></ag-input>
                    </div>
                    <app-user-reason-form
                        [hidden]="isLoading"
                        [disabled]="isSaving"
                        *ngIf="userReasonRequired"
                        (formReady)="formIsReady($event)"></app-user-reason-form>

                    <div [hidden]="!isLoading">
                        <div-placeholder-animation
                            [widthValue]="'16rem'"
                            [heightValue]="'0.8rem'">
                        </div-placeholder-animation>
                        <div-placeholder-animation></div-placeholder-animation>
                    </div>
                </ag-dialog-content>
                <ag-dialog-actions *ngIf="!showConfirmActionOnly">
                    <span *ngIf="isSaving">Please wait...</span>
                    <ag-loader
                        id="loading-spinner"
                        class="inline-progress"
                        [visible]="isSaving"
                        [small]="true"></ag-loader>
                    <ag-button
                        [disabled]="isLoading || isSaving"
                        type="secondary"
                        (click)="cancel()"
                        [value]="cancelButtonText"
                        id="no-btn"></ag-button>
                    <ag-button
                        [disabled]="!isConfirmButtonEnabled()"
                        [class.disabled]="!isConfirmButtonEnabled()"
                        type="primary"
                        (click)="confirm()"
                        [value]="confirmButtonText"
                        id="yes-btn"></ag-button>
                </ag-dialog-actions>
                <ag-dialog-actions *ngIf="showConfirmActionOnly">
                    <ag-button
                        type="primary"
                        (click)="cancel()"
                        [value]="infoButtonText"
                        id="ok-btn"></ag-button>
                </ag-dialog-actions>
            </div>
        </div>
    `,
    styles: [
        `
            :host .inline-progress > ::ng-deep .loader-overlay {
                background: transparent !important;
                margin: 0;
                padding: 0;
                position: static;
                transform: scale(0.7);
            }

            .modal-warning-header {
                display: flex;
                flex-direction: row;
                margin: 10px;
                padding: 5px;
                margin-left: 20px;
                margin-top: 40px;
            }

            .h2-title {
                font-size: 1.375rem;
                color: #53565A;
                margin-left: 20px;
                font-weight: 600;
                margin-top: 10px;
            }

            :host ::ng-deep .awf-icon {
                font-size: 40px;
            }

            .dialog-content {
                margin-left: 20px;
            }
        `
    ]
})

export class ProgressDialogComponent {

    dialogTitle: string;
    confirmButtonText: string;
    cancelButtonText: string;
    infoButtonText = 'OK';
    enableTextConfirmation: boolean;
    userReasonRequired = false;
    confirmationText = '';
    reasonForm: FormGroup;
    content: string;
    isLoading = false;
    isSaving = false;
    isWarningDialog = false;
    showConfirmActionOnly = false;
    task: any;
    taskArgs = [];

    constructor(
        private acApiService: ACApiService,
        private openLabSystemApiService: OpenLabSystemApiService,
        private activeModal: NgbActiveModal,
        @Inject(MODAL_DATA) private modalData: any) {
        this.dialogTitle = modalData.title;
        this.task = modalData.task;
        this.taskArgs = modalData.taskArgs;
        this.confirmButtonText = modalData.confirmButtonText || 'Yes';
        this.cancelButtonText = modalData.cancelButtonText || 'No';
        this.enableTextConfirmation = modalData.enableTextConfirmation;
        this.userReasonRequired = modalData.userReasonRequired || false;

        // TODO - Refactor to replace the if/else logic chain below with a more reasonable methodology
        if (this.dialogTitle === 'DELETE CID?') {
            const acToDelete = this.taskArgs[0];
            this.getConfirmAcDeleteMessage(acToDelete);
            this.userReasonRequired = true;
        }
        else if (this.dialogTitle === 'Reboot System' ||
            this.dialogTitle === 'Restart CDS Desktop' ||
            this.dialogTitle === 'Restart CID Agent') {
            const acToRestart = this.taskArgs[0];
            this.getConfirmRestartACMessage(acToRestart);
        }
        else if (this.dialogTitle === 'Disable CID software inheritance?') {
            const cidName = this.taskArgs[2];
            this.setConfirmCIDDisableInheritMessage(cidName);
            this.userReasonRequired = true;
        }
        else if (this.dialogTitle === 'Allow Changes to This CID?') {
            const cidName = this.taskArgs[1];
            this.setConfirmAllowCIDChangesMessage(cidName);
            this.taskArgs.pop();
            this.userReasonRequired = true;
        }
        else if (this.dialogTitle === 'Allow changes to the server?') {
            const serverName = this.taskArgs[1];
            this.setConfirmAllowServerChangesMessage(serverName);
            this.taskArgs.pop();
            this.userReasonRequired = true;
        }
        else {
            this.content = modalData.content;
            this.userReasonRequired = modalData.userReasonRequired;
        }
    }

    setConfirmAllowCIDChangesMessage(cidName: any) {
        this.isLoading = true;
        this.content = `Are you sure you want to allow changes for CID: ${cidName}?`;
        this.isLoading = false;
    }

    setConfirmCIDDisableInheritMessage(cidName: any) {
        this.isLoading = true;
        this.content = `Are you sure you want to disable server software inheritance for CID: ${cidName}?`;
        this.isLoading = false;
    }

    setConfirmAllowServerChangesMessage(serverName: any) {
        this.isLoading = true;
        this.content = `Are you sure you want to allow changes for server: ${serverName}?`;
        this.isLoading = false;
    }

    async getConfirmRestartACMessage(acToRestart) {
        this.isLoading = true;
        let confirmMessage = this.modalData.content;

        // Check if there is any connected instruments associated with the AC.
        // If confirm, add warning message.
        const hasConnectedInstruments = this.modalData.hasConnectedInstruments
        this.isWarningDialog = hasConnectedInstruments;
        if (hasConnectedInstruments) {
            const instrumentsWarningMessage = `One or more instruments are active on the CID ${acToRestart['name']}.\n\n`;
            confirmMessage = instrumentsWarningMessage + confirmMessage;
        }
        this.content = confirmMessage;

        this.isLoading = false;
    }

    async getConfirmAcDeleteMessage(acToDelete) {
        try {
            this.isLoading = true;
            let confirmMessage = `Are you sure you want to delete ${acToDelete['name']}?`;
            // Check if there is any instrument associated with the AC.
            // If confirm, add warning message.
            const hasInstruments = await this.acHasInstruments(acToDelete);
            this.isWarningDialog = hasInstruments;
            if (hasInstruments) {
                const instrumentsWarningMessage = `There are instruments associated with ${acToDelete['name']}.\n\n`;
                confirmMessage = instrumentsWarningMessage + confirmMessage;
            }
            this.content = confirmMessage;
        }
        catch (e) {
            console.error(e);
        }
        finally {
            this.isLoading = false;
        }
    }

    async confirm() {
        this.isSaving = true;
        if (this.userReasonRequired) {
            this.taskArgs.push(this.reasonForm.get('userReason').value);
        }
        let result = null;
        try {
            result = await this.task.call(this, ...this.taskArgs);
        } finally {
            this.isSaving = false;
            this.activeModal.close(result);
        }

    }

    cancel() {
        this.activeModal.dismiss();
    }

    async acHasInstruments(acToDelete) {
        // Get AC information from API and wait for response before moving on.
        const acResponse = await this.acApiService.getAC({
            acId: acToDelete.id,
            shadow_command: 'get_instruments_info'
        });
        const ac = acResponse['ac'];
        // If shadow_json is null, return false
        if (!ac.shadow_json) {
            return false;
        }

        const instrumentsList = ac.shadow_json['state']['reported']['instruments_info'];

        return (instrumentsList.length > 0);
    }

    async openLabServerHasAcs(serverToDelete: OpenLabSystem) {
        const openLabServerResponse = await this.openLabSystemApiService.getOpenLabSystemAcCount({
            openlab_system: serverToDelete
        });

        return (openLabServerResponse.ac_count > 0);
    }

    isConfirmButtonEnabled() {
        let isEnabled = !this.isLoading && !this.isSaving;
        if (this.enableTextConfirmation) {
            isEnabled = !this.isLoading
                && this.confirmationText.toLowerCase() === 'confirm';
        }
        if (this.userReasonRequired) {
            isEnabled = !this.isLoading && this.reasonForm && this.reasonForm.valid;
        }
        return isEnabled;
    }

    formIsReady(form: FormGroup) {
        this.reasonForm = form;
    }
}
